import React, { useRef, useState } from 'react';
import * as turf from '@turf/turf';
import L from 'leaflet';
import { MapContainer, TileLayer, Polygon, Polyline, Tooltip, CircleMarker } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';

import { FitBounds } from '../Map/Map';

import { PARTIES_COLORS } from '../../../util/constants/AppConstants';
import { formatWinInfo } from '../../../util/HelperFunctions';

import './ActivityMapLeaflet.scss';

const ActivityMapLeaflet = React.memo(({ polygons, messagePoints, mapType, cp, handlePolygonClick }) => {
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);

  const boundsFittedRef = useRef(false);

  const getInnerLine = (coordinates, offset = -0.00005) => {
    try {
      const line = turf.polygonToLine(turf.polygon([coordinates]));
      const innerLine = turf.lineOffset(line, offset, { units: 'degrees' });
      return innerLine.geometry.coordinates;
    } catch (e) {
      console.error('Failed to create inner line', e);
      return [];
    }
  };

  const getColorByCount = (count) => {
    if (count >= 30) return 'red';
    if (count >= 20) return 'orange';
    if (count >= 10) return 'yellow';
    if (count >= 1) return 'green';
    return 'lightslategrey';
  };

  const getColorByWinParty = (partyId) => {
    if (!partyId) return 'lightslategrey';

    return PARTIES_COLORS.find((elem) => elem.id === partyId).color;
  };

  const getBubbleRadius = (count) => {
    if (count >= 20) return 16;
    if (count >= 10) return 12;
    if (count >= 5) return 9;
    return 6;
  };

  const getBubbleColor = (count) => {
    if (count >= 20) return '#d73027';
    if (count >= 10) return '#fc8d59';
    if (count >= 5) return '#fee08b';
    return '#91cf60';
  };

  const getClusterTotalMessages = (cluster) => {
    return cluster.getAllChildMarkers().reduce((sum, marker) => {
      return sum + (marker.options.messageCount || 0);
    }, 0);
  };

  const getClusterSizeByMessages = (count) => {
    if (count >= 500) return 70;
    if (count >= 200) return 60;
    if (count >= 100) return 50;
    if (count >= 50) return 40;
    if (count >= 20) return 32;
    return 26;
  };

  const getClusterColorByMessages = (count) => {
    if (count >= 200) return '#d73027';
    if (count >= 100) return '#fc8d59';
    if (count >= 50) return '#fee08b';
    return '#91cf60';
  };

  const createClusterCustomIcon = (cluster) => {
    const totalMessages = getClusterTotalMessages(cluster);
    const size = getClusterSizeByMessages(totalMessages);
    const color = getClusterColorByMessages(totalMessages);

    return L.divIcon({
      html: `
        <div
          class="custom-cluster-bubble"
          style="
            width: ${size}px;
            height: ${size}px;
            background: ${color};
            font-size: ${size / 2.6}px;
          "
        >
          ${totalMessages}
        </div>
      `,
      className: 'custom-cluster-wrapper',
      iconSize: [size, size],
    });
  };

  return (
    <MapContainer
      style={{ width: '100%', height: '100%' }}
      zoom={mapZoom ?? 13}
      center={mapCenter ?? (cp.length > 0 ? cp : [0, 0])}
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {polygons.map((polygon) => {
        const color =
          mapType === 'Messages' ? getColorByCount(polygon.message_count) : getColorByWinParty(polygon.winner_party_id);
        const innerLineCoords = getInnerLine(polygon.coordinates);

        return (
          <React.Fragment key={polygon.id}>
            <Polygon
              positions={polygon.coordinates}
              pathOptions={{
                color,
                weight: 0,
                fillColor: color,
                fillOpacity: 0.6,
              }}
              eventHandlers={{
                click: () => handlePolygonClick(polygon.id),
              }}
            >
              <Tooltip sticky>
                {`${polygon.label} — ${mapType === 'Messages' ? `${polygon.message_count} message${polygon.message_count === 1 ? '' : 's'} from ${polygon.constituents_count} constituent${polygon.constituents_count === 1 ? '' : 's'}` : `${formatWinInfo(polygon.win_percentage, polygon.winner_party_name, polygon.second_party_name)}`}`}
              </Tooltip>
            </Polygon>
            {innerLineCoords.length > 0 && (
              <Polyline
                positions={innerLineCoords}
                pathOptions={{
                  color,
                  weight: 2,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
      {mapType === 'Messages' && messagePoints.length > 0 && (
        <MarkerClusterGroup
          chunkedLoading
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
          zoomToBoundsOnClick
          maxClusterRadius={50}
          iconCreateFunction={createClusterCustomIcon}
        >
          {messagePoints.map((p) => (
            <CircleMarker
              key={p.postal_code}
              center={[p.lat, p.lng]}
              radius={getBubbleRadius(p.message_count)}
              messageCount={p.message_count}
              pathOptions={{
                color: '#333',
                weight: 1,
                fillColor: getBubbleColor(p.message_count),
                fillOpacity: 0.85,
              }}
            >
              <Tooltip>
                <strong>{p.postal_code}</strong>
                <br />
                {p.message_count} message{p.message_count === 1 ? '' : 's'}
              </Tooltip>
            </CircleMarker>
          ))}
        </MarkerClusterGroup>
      )}
      <FitBounds
        polygons={polygons}
        boundsFittedRef={boundsFittedRef}
        setMapCenter={setMapCenter}
        setMapZoom={setMapZoom}
      />
    </MapContainer>
  );
});

export default ActivityMapLeaflet;
